import React, { useState, useEffect } from 'react';
import { CustomDialog } from '../Dialog';
import {
  Grid,
  Button,
  ButtonGroup,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PropTypes from 'prop-types';

/**
 * Dialog for configuring component metadata settings.
 * Allows users to enable timestamp placeholders (startTimestamp, endTimestamp)
 * that can be used to track workflow execution progress.
 */
export const ComponentMetadataSettingsDialog = ({
  open,
  onClose,
  componentId,
  metadataEnabled,
  startTimestampLabel,
  endTimestampLabel,
  metadataTimestampFormat,
  onSave,
}) => {
  // Local state for editing
  const [localMetadataEnabled, setLocalMetadataEnabled] = useState(metadataEnabled || false);
  const [localStartTimestampLabel, setLocalStartTimestampLabel] = useState(startTimestampLabel || '');
  const [localEndTimestampLabel, setLocalEndTimestampLabel] = useState(endTimestampLabel || '');
  const [localTimestampFormat, setLocalTimestampFormat] = useState(
    metadataTimestampFormat || 'yyyy-MM-dd HH:mm:ss'
  );
  const [startLabelError, setStartLabelError] = useState('');
  const [endLabelError, setEndLabelError] = useState('');

  // Sync local state when props change
  useEffect(() => {
    setLocalMetadataEnabled(metadataEnabled || false);
    setLocalStartTimestampLabel(startTimestampLabel || '');
    setLocalEndTimestampLabel(endTimestampLabel || '');
    setLocalTimestampFormat(metadataTimestampFormat || 'yyyy-MM-dd HH:mm:ss');
  }, [metadataEnabled, startTimestampLabel, endTimestampLabel, metadataTimestampFormat]);

  // Generate default labels based on component ID
  const generateDefaultLabels = () => {
    const last4Chars = componentId ? componentId.slice(-4) : '';
    setLocalStartTimestampLabel(`startTimestamp_${last4Chars}`);
    setLocalEndTimestampLabel(`endTimestamp_${last4Chars}`);
  };

  // Validate label - only alphanumeric and underscores allowed
  const validateLabel = (value) => {
    if (!value || value.length === 0) {
      return 'This field cannot be empty';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'No special characters or spaces allowed';
    }
    return '';
  };

  const handleStartLabelChange = (e) => {
    const value = e.target.value;
    setLocalStartTimestampLabel(value);
    setStartLabelError(validateLabel(value));
  };

  const handleEndLabelChange = (e) => {
    const value = e.target.value;
    setLocalEndTimestampLabel(value);
    setEndLabelError(validateLabel(value));
  };

  const handleEnableMetadata = (enabled) => {
    setLocalMetadataEnabled(enabled);
    if (enabled && !localStartTimestampLabel && !localEndTimestampLabel) {
      generateDefaultLabels();
    }
  };

  const handleSave = () => {
    // Validate before saving
    if (localMetadataEnabled) {
      const startError = validateLabel(localStartTimestampLabel);
      const endError = validateLabel(localEndTimestampLabel);
      
      if (startError || endError) {
        setStartLabelError(startError);
        setEndLabelError(endError);
        return;
      }
    }

    onSave({
      metadataEnabled: localMetadataEnabled,
      startTimestampLabel: localMetadataEnabled ? localStartTimestampLabel : '',
      endTimestampLabel: localMetadataEnabled ? localEndTimestampLabel : '',
      metadataTimestampFormat: localTimestampFormat,
    });
    onClose();
  };

  const isSaveDisabled = localMetadataEnabled && 
    (startLabelError || endLabelError || !localStartTimestampLabel || !localEndTimestampLabel);

  return (
    <CustomDialog
      isOpen={open}
      handleClose={onClose}
      title={<>Settings</>}
      contents={
        <Grid container direction="column" spacing={2}>
          {/* Component ID - Read Only */}
          <Grid item>
            <Grid container alignItems="center">
              <p className="s-text">Component ID</p>
              <Tooltip
                title="This is a unique identification code for this component."
                placement="right"
              >
                <HelpOutlineIcon className="custom-icon-btn" />
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              {componentId}
            </Grid>
          </Grid>

          {/* Enable Component Metadata Toggle */}
          <Grid item>
            <Grid container alignItems="center">
              <p className="s-text">Enable component metadata</p>
              <Tooltip
                title="Turn this on to expose component execution timestamps as placeholders that can be used in subsequent components."
                placement="right"
              >
                <HelpOutlineIcon className="custom-icon-btn" />
              </Tooltip>
            </Grid>
            <ButtonGroup>
              <Button
                className="d-button"
                color={localMetadataEnabled ? 'primary' : 'info'}
                variant={localMetadataEnabled ? 'contained' : 'outlined'}
                onClick={() => handleEnableMetadata(true)}
              >
                ON
              </Button>
              <Button
                className="d-button"
                color={localMetadataEnabled ? 'info' : 'primary'}
                variant={localMetadataEnabled ? 'outlined' : 'contained'}
                onClick={() => handleEnableMetadata(false)}
              >
                OFF
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Metadata fields - only shown when enabled */}
          {localMetadataEnabled && (
            <>
              {/* Start Timestamp Label */}
              <Grid item>
                <Grid container alignItems="center">
                  <p className="s-text">Start timestamp placeholder</p>
                  <Tooltip
                    title="The placeholder name for when this component started executing. Use this to track workflow progress."
                    placement="right"
                  >
                    <HelpOutlineIcon className="custom-icon-btn" />
                  </Tooltip>
                </Grid>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={localStartTimestampLabel}
                  onChange={handleStartLabelChange}
                  error={!!startLabelError}
                  helperText={startLabelError}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{'${'}</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{'}'}</InputAdornment>,
                  }}
                />
              </Grid>

              {/* End Timestamp Label */}
              <Grid item>
                <Grid container alignItems="center">
                  <p className="s-text">End timestamp placeholder</p>
                  <Tooltip
                    title="The placeholder name for when this component finished executing. Use this to track workflow progress."
                    placement="right"
                  >
                    <HelpOutlineIcon className="custom-icon-btn" />
                  </Tooltip>
                </Grid>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={localEndTimestampLabel}
                  onChange={handleEndLabelChange}
                  error={!!endLabelError}
                  helperText={endLabelError}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{'${'}</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{'}'}</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Timestamp Format */}
              <Grid item>
                <Grid container alignItems="center">
                  <p className="s-text">Timestamp format</p>
                  <Tooltip
                    title="Select the format for the timestamp values."
                    placement="right"
                  >
                    <HelpOutlineIcon className="custom-icon-btn" />
                  </Tooltip>
                </Grid>
                <Select
                  variant="outlined"
                  fullWidth
                  value={localTimestampFormat}
                  onChange={(e) => setLocalTimestampFormat(e.target.value)}
                >
                  <MenuItem value="yyyy-MM-dd'T'HH:mm:ssX">
                    yyyy-mm-dd'T'HH:mm:ssZ (2013-09-29T18:46:19Z)
                  </MenuItem>
                  <MenuItem value="dd MMMM yyyy HH:mm">
                    dd Month yyyy hh:mm (01 February 2021 14:01)
                  </MenuItem>
                  <MenuItem value="dd/MM/yy HH:mm">
                    dd/mm/yy hh:mm (15/12/21 14:01)
                  </MenuItem>
                  <MenuItem value="dd/MM/yyyy HH:mm">
                    dd/mm/yyyy hh:mm (15/12/2021 14:01)
                  </MenuItem>
                  <MenuItem value="dd-MM-yyyy HH:mm">
                    dd-mm-yyyy hh:mm (15-12-2021 14:01)
                  </MenuItem>
                  <MenuItem value="MM/dd/yyyy HH:mm">
                    mm/dd/yyyy hh:mm (12/15/2021 14:01)
                  </MenuItem>
                  <MenuItem value="yyyy-MM-dd HH:mm">
                    yyyy-mm-dd hh:mm (2021-02-01 14:01)
                  </MenuItem>
                  <MenuItem value="yyyy-MM-dd HH:mm:ss">
                    yyyy-mm-dd hh:mm:ss (2021-02-01 14:01:30)
                  </MenuItem>
                </Select>
              </Grid>
            </>
          )}
        </Grid>
      }
      buttons={
        <>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </>
      }
    />
  );
};

ComponentMetadataSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  componentId: PropTypes.string.isRequired,
  metadataEnabled: PropTypes.bool,
  startTimestampLabel: PropTypes.string,
  endTimestampLabel: PropTypes.string,
  metadataTimestampFormat: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

export default ComponentMetadataSettingsDialog;
